import React, { useState, useRef, useCallback } from 'react';
import { Package } from 'lucide-react';

/**
 * ImageThumbnail — shared component for list rows with hover preview.
 *
 * Props:
 *   src — image URL (thumb or full)
 *   fullSrc — optional larger image URL for hover preview (falls back to src)
 *   alt — alt text
 *   size — "sm" (32px) | "md" (40px) | "lg" (48px)
 *   className — additional classes
 *   fallbackIcon — Lucide icon component for placeholder (default: Package)
 */
export function ImageThumbnail({
  src,
  fullSrc,
  alt = '',
  size = 'md',
  className = '',
  fallbackIcon: FallbackIcon = Package,
  'data-testid': testId,
}) {
  const [imgError, setImgError] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPos, setPreviewPos] = useState({ top: 0, left: 0 });
  const thumbRef = useRef(null);
  const hideTimeout = useRef(null);

  const sizeMap = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };
  const sizeClass = sizeMap[size] || sizeMap.md;

  const hasValidImage = src && !imgError;
  const previewSrc = fullSrc || src;

  const handleMouseEnter = useCallback(() => {
    if (!hasValidImage) return;
    if (hideTimeout.current) clearTimeout(hideTimeout.current);

    if (thumbRef.current) {
      const rect = thumbRef.current.getBoundingClientRect();
      const previewW = 240;
      const previewH = 240;

      let left = rect.right + 8;
      let top = rect.top;

      // Avoid going off-screen right
      if (left + previewW > window.innerWidth - 16) {
        left = rect.left - previewW - 8;
      }
      // Avoid going off-screen bottom
      if (top + previewH > window.innerHeight - 16) {
        top = window.innerHeight - previewH - 16;
      }
      // Avoid going off-screen top
      if (top < 8) top = 8;

      setPreviewPos({ top, left });
    }
    setShowPreview(true);
  }, [hasValidImage]);

  const handleMouseLeave = useCallback(() => {
    hideTimeout.current = setTimeout(() => setShowPreview(false), 100);
  }, []);

  if (!hasValidImage) {
    return (
      <div
        className={`${sizeClass} rounded-lg bg-muted/50 flex items-center justify-center shrink-0 ${className}`}
        data-testid={testId}
      >
        <FallbackIcon className="w-4 h-4 text-muted-foreground/50" />
      </div>
    );
  }

  return (
    <>
      <div
        ref={thumbRef}
        className={`${sizeClass} rounded-lg overflow-hidden shrink-0 cursor-pointer ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={testId}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setImgError(true)}
        />
      </div>
      {showPreview && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{ top: previewPos.top, left: previewPos.left }}
        >
          <div className="w-60 h-60 rounded-xl overflow-hidden bg-background border shadow-xl">
            <img
              src={previewSrc}
              alt={alt}
              className="w-full h-full object-contain bg-muted/30"
              onError={() => setShowPreview(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ImageThumbnail;
